import type { CSSProperties, ReactNode } from "react";
import styles from "./Skeleton.module.scss";

type SkeletonProps = {
  width?: CSSProperties["width"];
  height?: CSSProperties["height"];
  radius?: CSSProperties["borderRadius"];
  /** Sets height from width, e.g. `"5 / 7"` for a card. */
  aspectRatio?: CSSProperties["aspectRatio"];
  className?: string;
  style?: CSSProperties;
};

/** Shimmering placeholder block. Decorative: wrap groups in `SkeletonGroup` for the status label. */
export function Skeleton({ width = "100%", height = "1em", radius, aspectRatio, className, style }: SkeletonProps) {
  return (
    <span
      aria-hidden="true"
      className={`${styles.skeleton} ${className ?? ""}`}
      style={{ width, height: aspectRatio ? undefined : height, aspectRatio, borderRadius: radius, ...style }}
    />
  );
}

/** Lines of text; the last one is shorter so it reads like a paragraph. */
export function SkeletonText({ lines = 3, lineHeight = "0.85rem", gap = "0.55rem" }: { lines?: number; lineHeight?: string; gap?: string }) {
  return (
    <span aria-hidden="true" className={styles.text} style={{ gap }}>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} height={lineHeight} width={i === lines - 1 && lines > 1 ? "62%" : "100%"} />
      ))}
    </span>
  );
}

/** Announces loading once for a whole skeleton layout. */
export function SkeletonGroup({ label, className, children }: { label: string; className?: string; children: ReactNode }) {
  return (
    <div role="status" aria-live="polite" aria-busy="true" className={className}>
      <span className={styles.srOnly}>{label}</span>
      {children}
    </div>
  );
}
